import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeftIcon, ChevronRightIcon, HeartIcon as HeartOutline } from '@heroicons/react/24/outline'
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid'
import { useSelection } from '../context/SelectionContext'
import AvailabilityBadge from './AvailabilityBadge'
import { ProductImage } from './ProductImage'
import { formatPrice } from '../utils'
import { KRAJ } from '../kraj'
import { useTranslation } from 'react-i18next'

const Card = ({ item, badge }) => {
  const { t } = useTranslation('glowna')
  const { productType, product } = item
  // Plakietka pokazuje sprzedawcę TEJ oferty. Wspólny napis dla całej karuzeli
  // przypisywałby wszystkie produkty jednemu sklepowi, odkąd jest ich dwóch.
  const etykieta = badge || product.seller
  const { isFavorite, toggleFavorite } = useSelection()
  const fav = isFavorite(productType, product.id)
  const gross = formatPrice(product.price, 0)
  return (
    <div className="relative rounded-xl border border-gray-100 bg-white p-3 hover:shadow-md transition-shadow flex flex-col">
      {etykieta && (
        <span className="absolute top-2 left-2 z-10 max-w-[70%] truncate rounded-md bg-blue-600 text-white text-[10px] font-semibold px-1.5 py-0.5">
          {etykieta}
        </span>
      )}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(productType, product) }}
        aria-label={t('karuzela.obserwuj')}
        className={`absolute top-2 right-2 z-10 ${fav ? 'text-rose-500' : 'text-gray-300 hover:text-rose-400'} transition-colors`}
      >
        {fav ? <HeartSolid className="w-5 h-5" /> : <HeartOutline className="w-5 h-5" />}
      </button>
      <Link to={`/${productType}/${product.id}`} className="flex flex-col flex-1" style={{ textDecoration: 'none', color: 'inherit' }}>
        <div className="h-28 flex items-center justify-center mb-3 mt-4">
          <ProductImage
            src={product.img_url}
            alt={product.name}
            productType={productType}
            className="max-h-28 max-w-full object-contain"
          />
        </div>
        <h3 className="text-sm font-semibold text-gray-800 leading-snug mb-2 line-clamp-2">{product.name}</h3>
        <div className="mt-auto">
          <p className="text-blue-600 font-bold text-lg">{gross !== null ? `${gross} ${KRAJ.symbol}` : t('karuzela.brak_ceny')}</p>
          {product.availability != null && String(product.availability).trim() !== '' && (
            <div className="mt-1"><AvailabilityBadge value={product.availability} /></div>
          )}
        </div>
      </Link>
    </div>
  )
}

const useVisibleCount = (maxVisible) => {
  const [n, setN] = useState(maxVisible)
  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth
      const responsive = w < 640 ? 1 : w < 768 ? 2 : w < 1024 ? 3 : 4
      setN(Math.min(responsive, maxVisible))
    }
    calc()
    window.addEventListener('resize', calc)
    return () => window.removeEventListener('resize', calc)
  }, [maxVisible])
  return n
}

const GRID = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' }

const FeaturedCarousel = ({ items = [], maxVisible = 4, badge = null }) => {
  const { t } = useTranslation('glowna')
  const visible = useVisibleCount(maxVisible)
  const [start, setStart] = useState(0)

  const len = items.length
  const canScroll = len > visible

  useEffect(() => { if (start >= len && len > 0) setStart(0) }, [len, start])

  useEffect(() => {
    if (!canScroll) return undefined
    const t = setInterval(() => setStart((s) => (s + 1) % len), 4000)
    return () => clearInterval(t)
  }, [canScroll, len])

  if (len === 0) return null

  const count = Math.min(visible, len)
  const windowItems = Array.from({ length: count }, (_, i) => items[(start + i) % len])
  const prev = () => setStart((s) => (s - 1 + len) % len)
  const next = () => setStart((s) => (s + 1) % len)

  return (
    <div className="relative">
      <div className={`grid gap-4 ${GRID[count] || 'grid-cols-1'}`}>
        {windowItems.map((it) => <Card key={`${it.productType}:${it.product.id}`} item={it} badge={badge} />)}
      </div>
      {canScroll && (
        <>
          <button
            onClick={prev}
            aria-label={t('karuzela.poprzednie')}
            className="absolute -left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-500 hover:text-blue-600 hover:border-blue-200 transition-colors"
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>
          <button
            onClick={next}
            aria-label={t('karuzela.nastepne')}
            className="absolute -right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-500 hover:text-blue-600 hover:border-blue-200 transition-colors"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  )
}

export default FeaturedCarousel
